/**
 * Search area container for composing and serializing multiple search field values.
 *
 * This component coordinates a dynamic set of search input components and
 * transforms their current values into a URL query string representation
 * that can be consumed by the surrounding search state or request layer.
 *
 * It exists to decouple search field rendering from the serialization logic
 * required by the backend-facing search string format. The component keeps
 * local field state, applies a debounce to user input changes, and emits
 * the final serialized search string through the provided setter callback.
 *
 * This module should be used whenever multiple heterogeneous search fields
 * must contribute to one normalized search query payload.
 *
 * @module
 *
 * @author Gregor Schalm gregor.schalm@wum-studios.de
 */

import { searchCombination, SearchFieldInterface } from "./SearchComponents/SearchFieldInterface";
import { SearchText } from "./SearchComponents/SearchText";
import { SearchSelect } from "./SearchComponents/SearchSelect";
import { UniRow } from "../../../../@WUM/core/Elements";
import { useEffect, useState } from "react";

/**
 * Properties required by the SearchArea component.
 *
 * @typedef {Object} SearchAreaProps
 * @property {SearchFieldInterface[]} searchFields - Collection of configured search fields that should be rendered.
 * @property {any} Store - Shared store object forwarded to child search components.
 * @property {(searchString: string) => void} setSearchString - Callback receiving the serialized query string after debounced changes.
 */
interface SearchAreaProps {
    searchFields: SearchFieldInterface[];
    Store: any;
    setSearchString: (searchString: string) => void;
}

/**
 * State entry for one individual search field.
 *
 * This structure keeps both the raw user-entered value and the selected
 * logical combination operator used for query serialization.
 *
 * @typedef {Object} SearchFieldValueState
 * @property {string} value - Current field value used for query serialization.
 * @property {searchCombination} combination - Logical operator defining how the field participates in the search query.
 */
interface SearchFieldValueState {
    value: string;
    combination: searchCombination;
}

/**
 * Renders the configured search fields and emits a serialized search query string.
 *
 * The component stores field values locally and converts them into the
 * expected URLSearchParams-based query format after a debounce interval.
 * Each field contributes a parameter name based on its logical combination.
 *
 * @param {SearchAreaProps} props - Component configuration and external search string setter.
 * @returns {JSX.Element} Rendered search field area.
 *
 * @example
 * <SearchArea
 *     searchFields={searchFields}
 *     Store={store}
 *     setSearchString={setSearchString}
 * />
 */
export const SearchArea = (props: SearchAreaProps): JSX.Element => {
    const { setSearchString } = props;

    const [searchFieldValues, setSearchFieldValues] = useState<Record<string, SearchFieldValueState>>({});

    /**
     * Updates the local state for a specific search field.
     *
     * The function merges the new field value into the existing field state
     * map while preserving all other field entries.
     *
     * @param {string} fieldName - Unique field name used as the state map key.
     * @param {string} value - New user-entered value for the field.
     * @param {searchCombination} combination - Logical operator assigned to the field.
     * @returns {void} This function does not return a value.
     *
     * @example
     * handleValueChange("status", "active", "and");
     */
    const handleValueChange = (
        fieldName: string,
        value: string,
        combination: searchCombination
    ): void => {
        setSearchFieldValues((previousValues) => ({
            ...previousValues,
            [fieldName]: {
                value,
                combination,
            },
        }));
    };

    useEffect(() => {
        const timeout: number = window.setTimeout(() => {
            const combinationMap: Record<searchCombination, string> = {
                and: "_a",
                not: "_n",
                or: "_o",
                like_after: "_la",
                like_before: "_lb",
                like_full: "_lf",
                not_like_after: "nla_",
                not_like_before: "nlb_",
                not_like_full: "nlf_",
            };

            const searchParams = new URLSearchParams();

            Object.entries(searchFieldValues).forEach(
                ([fieldName, fieldValueState]: [string, SearchFieldValueState]) => {
                    const combiner = combinationMap[fieldValueState.combination];
                    const parameterName = `s_${fieldName}${combiner}`;

                    searchParams.set(parameterName, fieldValueState.value);
                }
            );

            const searchString = searchParams.toString();

            setSearchString(searchString);
        }, 600);

        /**
         * Clears the pending debounce timer before the effect reruns or the component unmounts.
         *
         * This prevents outdated search string updates from being emitted.
         *
         * @returns {void} This cleanup function does not return a value.
         */
        return (): void => {
            window.clearTimeout(timeout);
        };
    }, [searchFieldValues, setSearchString]);

    return (
        <>
            <UniRow>
                {props.searchFields.map((field) => {
                    switch (field.type) {
                        case "text":
                            return (
                                <SearchText
                                    key={field.name}
                                    type={field.type}
                                    name={field.name}
                                    Store={props.Store}
                                    value={searchFieldValues[field.name]?.value ?? field.defaultValue ?? ""}
                                    onValueChange={handleValueChange}
                                    label={field.label}
                                />
                            );

                        case "select":
                            return (
                                <SearchSelect
                                    key={field.name}
                                    Store={props.Store}
                                    value={searchFieldValues[field.name]?.value ?? field.defaultValue ?? ""}
                                    name={field.name}
                                    label={field.label}
                                    options={field.options}
                                    freeValue={field.freeValue}
                                    freeValueLabel={field.freeValueLabel}
                                    onValueChange={handleValueChange}
                                    type={"select"}
                                />
                            );

                        default:
                            return null;
                    }
                })}
            </UniRow>
        </>
    );
};